import { RoleName } from 'chaika-enums';

import { DurationType } from '@common/CreationMenuFields/Duration/Duration';
import { LegalEntityStatus, LegalEntityType } from 'chaika-enums';
import { AntdOptionType } from '@utils/makeOptionTypes';
import { FieldTitleTheme } from '@components/FieldTitle';
import styles from '@components/Table/CreationMenu/CreationMenuField/CreationMenuField.module.scss';

export interface TableField<OptionValue = any> {
    label?: string;
    dataTest: string;
    value?: any;
    placeholder?: string;
    isClearable?: boolean;
    /** Minimum selectable date for the `DateField` field type. */
    minDate?: Date;
    fieldName: string;
    valueType?: DurationType | 'number' | 'text' | 'tel';
    type: TableFieldType;
    modifiers?: any[];
    depByFields?: DependencyByField;
    options?: AntdOptionType<OptionValue>[];
    loadOptions?: (inputValue: string, params?: any) => Promise<AntdOptionType[]>;
    multiFields?: TableField[];
    fieldModifiers?: FieldModifier[];
    multiFieldModifiers?: MultiFieldModifier[];
    fieldTitleTheme?: FieldTitleTheme;
    customStyle?: string;
    resetValueOnUpdateOption?: boolean;
    disabled?: boolean;
    isNotUpdated?: boolean;
    validationParams?: ValidationParams;
    /** Label format flags for `AsyncUserSelect` / `AsyncUserMultiSelect` field types. */
    withFullName?: boolean;
    withInitials?: boolean;
    roleNames?: RoleName[];
    /** Scope for `AsyncLegalEntitySelect` / `AsyncLegalEntityMultiSelect` field types. */
    legalEntityScope?: LegalEntityFieldScope;
    getOptions?(): AntdOptionType[];
    onChangeValue?(value: any): Promise<void>;
}

/** Scope for the `AsyncLegalEntitySelect` / `AsyncLegalEntityMultiSelect` field types. */
export interface LegalEntityFieldScope {
    /** Type scope for default options. @default [LegalEntityType.Clinic] */
    types?: LegalEntityType[];
    /** Status scope for default options. @default [LegalEntityStatus.Active] */
    statuses?: LegalEntityStatus[];
    /** Type scope for typed search. @default [] (unscoped) */
    searchTypes?: LegalEntityType[];
    /** Status scope for typed search. @default [] (unscoped) */
    searchStatuses?: LegalEntityStatus[];
}

export interface DependencyByField {
    actionType: ActionTypes[];
    dependencies: Dependency[];
}

interface Dependency {
    field: string;
    defaultFunctionResult?: DefaultFunctionResult;
    getOptions?(menuData: object): AntdOptionType[];
    calculateValue?(menuData: object): any;
    resetFunction?(): void;
    checkFunction?(dependencyFields: TableField[]): boolean;
}

export enum DefaultFunctionResult {
    Positive = 'positive',
    Negative = 'negative',
}

export enum ActionTypes {
    Disabled = 'disabled',
    Hidden = 'hidden',
    OptionsByField = 'optionsByField',
    CalculateValueByField = 'valueByField',
}

export enum FieldModifier {
    // @ts-ignore
    NoWrapContent = styles.noWrapContent,
    // @ts-ignore
    ThirdPartWidth = styles.thirdPartWidth,
}

export enum MultiFieldModifier {
    // @ts-ignore
    MultiFieldFullLine = styles.fullLineField,
}

export interface ValidationParams {
    unValidText?: string;
    validationFunction(value: any): boolean;
}

export enum TableFieldType {
    Select = 'select',
    AsyncSelect = 'asyncSelect',
    AsyncMultiSelect = 'asyncMultiSelect',
    AsyncUserSelect = 'asyncUserSelect',
    AsyncUserMultiSelect = 'asyncUserMultiSelect',
    AsyncLegalEntitySelect = 'asyncLegalEntitySelect',
    AsyncLegalEntityMultiSelect = 'asyncLegalEntityMultiSelect',
    Money = 'money',
    Status = 'status',
    Currency = 'currency',
    Checkbox = 'checkbox',
    Text = 'text',
    SearchMultiSelect = 'searchMultiSelect',
    DateField = 'date',
    MultiField = 'multiField',
    FieldTitle = 'fieldTitle',
    Input = 'input',
    TextArea = 'textArea',
    TranslationEditor = 'translationEditor',
    Duration = 'duration',
    BasePriceEditor = 'basePriceEditor',
    CompositePriceEditor = 'compositePriceEditor',
    EhrInsuranceInfo = 'ehrInsuranceInfo',
    OrganizationAssignment = 'organizationAssignment',
    DiscountEditor = 'discountEditor',
}
